// ENG-5635: decide whether a primary_model change should trigger an immediate
// session respawn.
//
// Background: subscription Claude Code agents only pick up their model via the
// `claude --model <alias>` flag the launcher passes at spawn time (ENG-5631).
// Changing primary_model rewrites provision/settings.json and the manager's
// in-memory knownModels map, but does NOT restart the running session — so the
// new model is invisible until the next natural respawn (daily rotation,
// channel/MCP/auth change, crash, or manual restart). This predicate gates a
// proactive hot-reload respawn so the operator's choice applies within a poll.
//
// Kept as a pure function so the gating logic is unit-testable without the
// manager-worker module graph.

export interface ModelChangeRespawnInput {
  /** The model the manager last saw for this agent (knownModels). Undefined/null on first provision. */
  previousModel: string | null | undefined;
  /** The freshly-resolved model for this poll. */
  primaryModel: string | null | undefined;
  /** The agent's framework id (e.g. 'claude-code', 'openclaw', 'nemoclaw'). */
  framework: string;
  /** Whether a healthy persistent session is currently running for this agent. */
  sessionHealthy: boolean;
}

/**
 * True when the model change warrants restarting the live session now.
 *
 * Returns false (let the next natural spawn carry the new model) when:
 * - the framework isn't claude-code — `--model` is a Claude Code launch flag;
 *   other runtimes apply model changes through their own config/gateway path;
 * - no healthy session is running — the upcoming spawn already reads the new
 *   model, so a restart would be pointless (or fight the spawn);
 * - `previousModel` is unset — first provision; the imminent first spawn
 *   carries the model, nothing to switch;
 * - `primaryModel` is unset — the model couldn't be resolved this poll;
 *   respawning would only drop the agent to the tier default, so leave the
 *   live session on its current (known-good) model;
 * - the model is unchanged — no-op, avoids a restart loop.
 */
export function shouldRespawnForModelChange(input: ModelChangeRespawnInput): boolean {
  const { previousModel, primaryModel, framework, sessionHealthy } = input;
  if (framework !== 'claude-code') return false;
  if (!sessionHealthy) return false;
  if (!previousModel) return false;
  if (!primaryModel) return false;
  return previousModel !== primaryModel;
}
